// Suite saga — orchestrates batch execution of all scenarios.
//
// Reads scenarios from scenarioSourceSlice (already loaded by scenarioSaga).
// Runs each scenario sequentially through the walker, captures results.
// Every scenario in the manifest produces a row: run scenarios get a real
// verdict derived from run status + assertion summary; scenarios that could
// not be run are recorded as explicit 'skipped' rows with a reason — never
// silently dropped. The suite always finishes (try/finally), so the UI can
// never wedge in "Running…".

import { put, take, select, call, takeLatest, delay } from 'redux-saga/effects';
import { loadTopology } from '../slices/topologySlice.ts';
import { loadScenario } from '../actions/loadScenario.ts';
import {
  suiteStarted,
  suiteScenarioCompleted,
  suiteFinished,
  resetCumulativeCoverage,
  selectAssertionSummary,
  selectSimulationStatus,
  computeScenarioVerdict,
  emptyAssertionSummary,
  type AssertionSummary,
} from '../slices/simulationSlice.ts';
import type { SimulationStatus } from '../../types/simulation-results.ts';
import { selectScenarioLabel } from '../slices/eventsSlice.ts';
import {
  selectManifest,
  selectActiveSource,
  selectActiveSourceState,
} from '../slices/scenarioSourceSlice.ts';
import {
  fetchScenario,
  fetchTopology,
  getCachedTopology,
} from '../../services/RemoteScenarioLoader.ts';
import type { FlowManifest, ManifestEntry, SimulationScenario } from '../../types/index.ts';

export const SUITE_RUN_ALL = 'suite/runAll' as const;

/**
 * Per-scenario macrotask yield. The walker's instant run is fully synchronous,
 * so without this the whole suite completes inside a single scheduler flush:
 * React never paints the suiteStarted() row reset nor any progress, and
 * "▶ REPLAY ALL" looks like a no-op even though it re-ran everything
 * (owner-reported v2 bug). A small real delay lets the ledger visibly reset
 * and repopulate row by row. Headless harnesses (facet-sanity / facet-e2e)
 * poll with waitFor, so the added ~15ms per scenario is immaterial there.
 */
export const SUITE_SCENARIO_YIELD_MS = 15;

export function suiteRunAll() {
  return { type: SUITE_RUN_ALL } as const;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- redux-saga generators require `any`
function* recordSkipped(entry: ManifestEntry, reason: string): Generator<any, void, any> {
  yield put(
    suiteScenarioCompleted({
      scenarioId: entry.scenarioId,
      scenarioLabel: entry.scenarioLabel,
      verdict: 'skipped',
      reason,
      assertionSummary: emptyAssertionSummary(),
      durationMs: 0,
    }),
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- redux-saga generators require `any`
function* runSuite(): Generator<any, void, any> {
  yield put(resetCumulativeCoverage());
  yield put(suiteStarted());

  try {
    // Read manifest and cached scenarios from the active source
    const manifest: FlowManifest | null = yield select(selectManifest);
    if (!manifest || manifest.flows.length === 0) {
      return;
    }

    const activeSource: string = yield select(selectActiveSource);
    const sourceState = yield select(selectActiveSourceState);
    const loadedScenarios: Record<string, SimulationScenario> = sourceState.loadedScenarios;

    // Run each scenario across all flows sequentially
    for (const flow of manifest.flows) {
      // Load the flow's topology (cached after first fetch). A flow whose
      // topology can't be loaded still produces explicit skipped rows for
      // each of its scenarios.
      let topologyOk = true;
      let topologyReason = '';
      if (activeSource === 'example') {
        try {
          const topo = getCachedTopology(flow.flowId) ?? (yield call(fetchTopology, flow));
          yield put(loadTopology(topo));
        } catch {
          topologyOk = false;
          topologyReason = `topology for flow "${flow.flowName}" could not be loaded`;
        }
      } else {
        const topoFromSlice = sourceState.loadedTopologies?.[flow.flowId];
        if (!topoFromSlice) {
          topologyOk = false;
          topologyReason = `topology for flow "${flow.flowName}" is not uploaded`;
        } else {
          yield put(loadTopology(topoFromSlice));
        }
      }

      for (const entry of flow.scenarios) {
        // Yield a macrotask so the UI can paint suite progress (see
        // SUITE_SCENARIO_YIELD_MS above).
        yield delay(SUITE_SCENARIO_YIELD_MS);

        if (!topologyOk) {
          yield* recordSkipped(entry, topologyReason);
          continue;
        }

        // Use cached scenario, or fetch it if it's the example source
        let seed = loadedScenarios[entry.scenarioId];
        if (!seed && activeSource === 'example') {
          try {
            seed = yield call(fetchScenario, entry);
          } catch {
            yield* recordSkipped(entry, 'scenario could not be fetched');
            continue;
          }
        }
        if (!seed) {
          yield* recordSkipped(entry, 'scenario is not loaded for the active source');
          continue;
        }

        const startTime = Date.now();

        yield put(loadScenario({ seed }));

        // Wait for the walker saga to finish (completed or failed)
        yield take(['simulation/simulationCompleted', 'simulation/simulationFailed']);

        const durationMs = Date.now() - startTime;
        const assertionSummary: AssertionSummary = yield select(selectAssertionSummary);
        const status: SimulationStatus = yield select(selectSimulationStatus);
        const scenarioLabel: string = yield select(selectScenarioLabel);

        // Verdict derives from run status + summary — an aborted run reads
        // as 'error', a completed run with nothing evaluated as
        // 'inconclusive'. NEVER `failed === 0 → pass`.
        const verdict = computeScenarioVerdict(status, assertionSummary) ?? 'error';

        yield put(
          suiteScenarioCompleted({
            scenarioId: entry.scenarioId,
            scenarioLabel: scenarioLabel ?? entry.scenarioLabel,
            verdict,
            reason:
              verdict === 'error'
                ? 'run aborted before completing — assertions are incomplete'
                : verdict === 'inconclusive'
                  ? 'run completed but no assertions were evaluated'
                  : undefined,
            assertionSummary: { ...assertionSummary },
            durationMs,
          }),
        );
      }
    }
  } finally {
    yield put(suiteFinished());
  }
}

export default function* suiteSaga() {
  yield takeLatest(SUITE_RUN_ALL, runSuite);
}
